import { supabase } from "./supabase";

// ── Tier ordering ─────────────────────────────────────────────────────────────
// Used to determine whether a tier change is an upgrade or downgrade.
// Prices must match subscription_tiers.price_monthly in the DB.
const TIER_PRICE: Record<string, number> = {
  seedling: 0,
  ember: 1.99,
  keeper: 4.99,
  storyteller: 9.99,
};

function isUpgrade(from: string, to: string): boolean {
  return (TIER_PRICE[to] ?? 0) > (TIER_PRICE[from] ?? 0);
}

function isDowngrade(from: string, to: string): boolean {
  return (TIER_PRICE[to] ?? 0) < (TIER_PRICE[from] ?? 0);
}

/** Add exactly 1 calendar month to a date, e.g. Jan 31 → Feb 28. */
function addOneMonth(from: Date): Date {
  const d = new Date(from);
  d.setMonth(d.getMonth() + 1);
  return d;
}

// ── upgradeTier ───────────────────────────────────────────────────────────────
/**
 * Immediately upgrades the user's subscription to `newTierId`.
 * - New tier is active right away.
 * - Period resets to now() → now() + 1 month.
 * - Any pending scheduled downgrade is cleared.
 * - Writes an 'upgrade' row to subscription_change_log.
 */
export async function upgradeTier(
  userId: string,
  newTierId: string,
): Promise<void> {
  const { data: sub, error: subErr } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("user_id", userId)
    .single();

  if (subErr || !sub) {
    throw new Error("Could not fetch subscription");
  }
  if (sub.tier_id === newTierId) {
    throw new Error("Already on this tier");
  }
  if (!isUpgrade(sub.tier_id, newTierId)) {
    throw new Error(
      `Moving from ${sub.tier_id} to ${newTierId} is not an upgrade — use downgradeTier instead`,
    );
  }

  const now = new Date();
  const periodEnd = addOneMonth(now);

  const { error: updateErr } = await supabase
    .from("subscriptions")
    .update({
      tier_id: newTierId,
      current_period_start: now.toISOString(),
      current_period_end: periodEnd.toISOString(),
      last_changed_at: now.toISOString(),
      scheduled_tier_id: null,
      scheduled_at: null,
      status: "active",
    })
    .eq("user_id", userId);

  if (updateErr) {
    throw new Error(`Failed to upgrade: ${updateErr.message}`);
  }

  const { error: logErr } = await supabase
    .from("subscription_change_log")
    .insert({
      user_id: userId,
      from_tier_id: sub.tier_id,
      to_tier_id: newTierId,
      change_type: "upgrade",
      effective_at: now.toISOString(),
      scheduled_for: null,
    });

  if (logErr) {
    console.error("[subscriptionHelpers] Failed to log upgrade:", logErr.message);
  }
}

// ── downgradeTier ─────────────────────────────────────────────────────────────
/**
 * Schedules a downgrade to `newTierId` at the end of the current billing period.
 * - The user keeps their current tier until current_period_end.
 * - Sets scheduled_tier_id and scheduled_at.
 * - Does NOT change tier_id or current_period_end.
 * - Writes a 'downgrade' row to subscription_change_log.
 */
export async function downgradeTier(
  userId: string,
  newTierId: string,
): Promise<void> {
  const { data: sub, error: subErr } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("user_id", userId)
    .single();

  if (subErr || !sub) {
    throw new Error("Could not fetch subscription");
  }
  if (sub.tier_id === newTierId) {
    throw new Error("Already on this tier");
  }
  if (!isDowngrade(sub.tier_id, newTierId)) {
    throw new Error(
      `Moving from ${sub.tier_id} to ${newTierId} is not a downgrade — use upgradeTier instead`,
    );
  }

  const now = new Date();

  const { error: updateErr } = await supabase
    .from("subscriptions")
    .update({
      scheduled_tier_id: newTierId,
      scheduled_at: now.toISOString(),
      last_changed_at: now.toISOString(),
    })
    .eq("user_id", userId);

  if (updateErr) {
    throw new Error(`Failed to schedule downgrade: ${updateErr.message}`);
  }

  const { error: logErr } = await supabase
    .from("subscription_change_log")
    .insert({
      user_id: userId,
      from_tier_id: sub.tier_id,
      to_tier_id: newTierId,
      change_type: "downgrade",
      effective_at: null,
      scheduled_for: sub.current_period_end ?? null,
    });

  if (logErr) {
    console.error("[subscriptionHelpers] Failed to log downgrade:", logErr.message);
  }
}

// ── cancelDowngrade ───────────────────────────────────────────────────────────
/**
 * Cancels a previously scheduled downgrade.
 * - Clears scheduled_tier_id and scheduled_at.
 * - Writes a 'cancelled_downgrade' row to subscription_change_log.
 */
export async function cancelDowngrade(userId: string): Promise<void> {
  const { data: sub, error: subErr } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("user_id", userId)
    .single();

  if (subErr || !sub) {
    throw new Error("Could not fetch subscription");
  }
  if (!sub.scheduled_tier_id) {
    throw new Error("No scheduled downgrade to cancel");
  }

  const now = new Date();

  const { error: updateErr } = await supabase
    .from("subscriptions")
    .update({
      scheduled_tier_id: null,
      scheduled_at: null,
      last_changed_at: now.toISOString(),
    })
    .eq("user_id", userId);

  if (updateErr) {
    throw new Error(`Failed to cancel downgrade: ${updateErr.message}`);
  }

  const { error: logErr } = await supabase
    .from("subscription_change_log")
    .insert({
      user_id: userId,
      from_tier_id: sub.tier_id,
      to_tier_id: sub.scheduled_tier_id,
      change_type: "cancelled_downgrade",
      effective_at: null,
      scheduled_for: null,
    });

  if (logErr) {
    console.error("[subscriptionHelpers] Failed to log cancellation:", logErr.message);
  }
}
